import { useEffect, useRef } from "react";
import { applyZoomAnchor, centreZoomAnchor } from "../calendar-zoom-anchor";

export function useDayViewScroll({
  scrollAreaRef,
  nowAnchorRef,
  previousZoomPercentRef,
  initialScrollToNow,
  initialScrollRatio,
  initialSelectedEventId,
  onDidInitialScroll,
  onSaveScrollRatio,
  zoomPercent,
  widthZoomPercent = 100,
  zoomAnchorRef,
  resetSignal = 0,
  focusEventId = null,
  focusSignal = 0,
}) {
  const previousWidthZoomPercentRef = useRef(widthZoomPercent);
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return;
    }

    const scrollToPosition = () => {
      // An Activity re-show re-runs this "mount-only" effect, but the browser kept the
      // viewport's exact offsets through display:none — any re-derived position (now anchor,
      // focused event, saved center-ratio) would MOVE the view away from where the user left
      // it. Only a genuinely fresh viewport (both offsets 0) gets an initial scroll; if the
      // engine ever resets offsets on hide, the ratio fallback below still restores.
      if (viewport.scrollTop > 0 || viewport.scrollLeft > 0) {
        onDidInitialScroll?.();
        return;
      }
      if (initialScrollToNow) {
        nowAnchorRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
        onDidInitialScroll?.();
        return;
      }
      if (initialSelectedEventId) {
        const eventEl = viewport.querySelector(`[data-event-id="${initialSelectedEventId}"]`);
        if (eventEl) {
          eventEl.scrollIntoView({ block: "center", behavior: "auto" });
        }
        onDidInitialScroll?.();
        return;
      }
      if (initialScrollRatio != null && initialScrollRatio >= 0 && initialScrollRatio <= 1) {
        const scrollHeight = Math.max(viewport.scrollHeight, 1);
        const centerPx = initialScrollRatio * scrollHeight;
        const scrollTop = centerPx - viewport.clientHeight / 2;
        const maxScrollTop = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
        viewport.scrollTop = Math.min(Math.max(scrollTop, 0), maxScrollTop);
      }
      onDidInitialScroll?.();
    };

    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToPosition);
    });
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only initial scroll
  }, []);

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport || typeof onSaveScrollRatio !== "function") {
      return;
    }
    const updateSavedRatio = () => {
      // A hidden viewport (Activity display:none — including this effect's own cleanup, which
      // runs AFTER the hide commit) measures 0x0 with scrollTop reset; saving that would
      // overwrite the real position with ~0 and every thaw would restore to the top.
      if (viewport.clientHeight === 0) return;
      const scrollHeight = Math.max(viewport.scrollHeight, 1);
      const centerPx = viewport.scrollTop + viewport.clientHeight / 2;
      onSaveScrollRatio(Math.min(Math.max(centerPx / scrollHeight, 0), 1));
    };
    updateSavedRatio();
    viewport.addEventListener("scroll", updateSavedRatio, { passive: true });
    return () => {
      viewport.removeEventListener("scroll", updateSavedRatio);
      updateSavedRatio();
    };
  }, [onSaveScrollRatio]);

  useEffect(() => {
    if (previousZoomPercentRef.current === zoomPercent) {
      return;
    }

    const scrollAreaViewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!scrollAreaViewport) {
      previousZoomPercentRef.current = zoomPercent;
      return;
    }

    // Anchor the zoom: pin the point under the cursor (wheel zoom, anchor present
    // for the vertical axis) — or the viewport centre (Display-popover zoom, no
    // anchor) — to the same on-screen offset. Both are expressed grid-relative
    // and resolved against measured rects (see calendar-zoom-anchor.js); the old
    // rem-derived px-per-minute drifted wherever `1fr` rows stretched.
    const anchorObj = zoomAnchorRef?.current;
    const useCursor = !!(anchorObj && anchorObj.view === "day" && anchorObj.axes?.vertical);
    const measured = useCursor ? anchorObj.measured : null;
    const fallback = measured ? null : centreZoomAnchor(scrollAreaViewport);
    if (anchorObj && anchorObj.view === "day" && anchorObj.axes) {
      anchorObj.axes.vertical = false;
      if (!anchorObj.axes.horizontal) {
        zoomAnchorRef.current = null;
      }
    }

    window.requestAnimationFrame(() => {
      applyZoomAnchor(scrollAreaViewport, measured || fallback, { vertical: true });
    });

    previousZoomPercentRef.current = zoomPercent;
  }, [zoomPercent]);

  // Horizontal ("width") zoom compensation: keep the column under the cursor (or the viewport
  // centre) anchored as columns widen/narrow. Ratio-based on scrollWidth.
  useEffect(() => {
    if (previousWidthZoomPercentRef.current === widthZoomPercent) {
      return;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      previousWidthZoomPercentRef.current = widthZoomPercent;
      return;
    }
    const anchorObj = zoomAnchorRef?.current;
    const useCursor = !!(anchorObj && anchorObj.view === "day" && anchorObj.axes?.horizontal);
    const measured = useCursor ? anchorObj.measured : null;
    const fallback = measured ? null : centreZoomAnchor(viewport);
    if (anchorObj && anchorObj.view === "day" && anchorObj.axes) {
      anchorObj.axes.horizontal = false;
      if (!anchorObj.axes.vertical) {
        zoomAnchorRef.current = null;
      }
    }
    const id = window.requestAnimationFrame(() => {
      applyZoomAnchor(viewport, measured || fallback, { horizontal: true });
    });
    previousWidthZoomPercentRef.current = widthZoomPercent;
    return () => window.cancelAnimationFrame(id);
  }, [widthZoomPercent]);

  // Deep link ("show this appointment on the calendar"): scroll the requested card into
  // view whenever the signal bumps. Deliberately NOT the mount-only effect above — that one
  // stands down on an already-scrolled viewport (the Activity re-show guard), which is
  // exactly the case when the user jumps in from another page onto a day view they had
  // already scrolled. Double-rAF so the card exists and the rows are at their final height.
  useEffect(() => {
    if (!focusSignal || !focusEventId) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const eventEl = viewport.querySelector(`[data-event-id="${CSS.escape(focusEventId)}"]`);
        eventEl?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
      });
    });
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the signal is the trigger
  }, [focusSignal]);

  // Quick-navigation reset: re-scroll to "now". Double-rAF so it lands AFTER the 100%-zoom
  // relayout (and after the zoom-change effect's own rAF), making the reset the final word.
  useEffect(() => {
    if (!resetSignal) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        viewport.scrollLeft = 0;
        nowAnchorRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [resetSignal]);
}
